import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Mail, Link as LinkIcon, Copy, Loader2, Sparkles, Send, CheckCircle2, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { useEmailCredentials } from "@/hooks/useEmailCredentials";
import { useEmailSignatures } from "@/hooks/useEmailSignatures";
import { useTeamAgents } from "@/hooks/useTeamAgents";
import { supabase } from "@/integrations/supabase/client";

// Helper function to convert **bold** markdown and URLs to HTML
const formatText = (text: string) => {
  // URL regex that matches URLs (may include trailing punctuation)
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Common trailing punctuation that should be excluded from URLs
  const trailingPunctuation = /([.,;:!?\)\]\}]+)$/;
  
  // First split by URLs
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    // If it's a URL, create a link
    if (part.match(urlRegex)) {
      // Check if URL has trailing punctuation
      const punctuationMatch = part.match(trailingPunctuation);
      if (punctuationMatch) {
        // Split URL and trailing punctuation
        const cleanUrl = part.replace(trailingPunctuation, '');
        const punctuation = punctuationMatch[1];
        return (
          <span key={index}>
            <a 
              href={cleanUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {cleanUrl}
            </a>
            {punctuation}
          </span>
        );
      }
      // No trailing punctuation, create link as-is
      return (
        <a 
          key={index} 
          href={part} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {part}
        </a>
      );
    }
    
    // Otherwise, process for bold text
    return part.split(/(\*\*.*?\*\*)/).map((subPart, subIndex) => {
      if (subPart.startsWith('**') && subPart.endsWith('**')) {
        return <strong key={`${index}-${subIndex}`}>{subPart.slice(2, -2)}</strong>;
      }
      return subPart;
    });
  });
};

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  northData: any;
  northId: string;
}

export const ShareDialog = ({ open, onOpenChange, northData, northId }: ShareDialogProps) => {
  const { toast } = useToast();
  const [emailContent, setEmailContent] = useState("");
  const [displayedContent, setDisplayedContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendScope, setSendScope] = useState<"team" | "agent">("team");
  const { hasCredentials, loading: credentialsLoading } = useEmailCredentials();
  const { agents } = useTeamAgents();

  // Find the agent assigned to this North
  const agentName = northData?.custom_agent_name || null;
  const matchedAgent = useMemo(
    () => (agentName ? agents.find((a) => a.name === agentName) : null),
    [agentName, agents]
  );

  // Fetch signatures — include agent's ID if the North has an assigned agent
  const { teamSignature, agentSignature } = useEmailSignatures(matchedAgent?.id || null);

  // Current signature based on sendScope
  const currentSignature = sendScope === "agent" && agentSignature
    ? agentSignature
    : teamSignature;

  // Default sendScope to agent if agent sig exists, else team
  useEffect(() => {
    if (matchedAgent && agentSignature) {
      setSendScope("agent");
    } else {
      setSendScope("team");
    }
  }, [matchedAgent?.id, agentSignature?.id]);

  const reportUrl = `${window.location.origin}/north/${northId}`;
  const isLot = /lot|land/i.test(northData?.property_type || "");
  const emailSubject = `Market Update & Strategic Insights for Your ${isLot ? "Lot" : "Home"} Listing`;

  // Typing animation effect
  useEffect(() => {
    if (emailContent && !isGenerating) {
      setIsTyping(true);
      let currentIndex = 0;
      const typingSpeed = 15; // milliseconds per character
      
      const typeNextCharacter = () => {
        if (currentIndex < emailContent.length) {
          setDisplayedContent(emailContent.substring(0, currentIndex + 1));
          currentIndex++;
          setTimeout(typeNextCharacter, typingSpeed);
        } else {
          setIsTyping(false);
        }
      };
      
      typeNextCharacter();
    }
  }, [emailContent, isGenerating]);

  // Auto-generate email when dialog opens
  useEffect(() => {
    if (open && !emailContent && !isGenerating) {
      generateEmail();
    }
    if (open && northData?.seller_email && !recipientEmail) {
      setRecipientEmail(northData.seller_email);
    }
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setEmailContent("");
      setDisplayedContent("");
      setIsTyping(false);
      setSendSuccess(false);
    }
  }, [open]);

  const generateEmail = async () => {
    setIsGenerating(true);
    setIsTyping(false);
    setEmailContent("");
    setDisplayedContent("");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            northData,
            reportUrl,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate email");
      }

      const data = await response.json();
      setEmailContent(data.content);
    } catch (error) {
      logger.error("Email generation error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate email",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Convert markdown to HTML for email clients
  const markdownToHtml = (text: string | null | undefined): string => {
    // Ensure we have a string
    if (!text || typeof text !== 'string') {
      return '';
    }
    
    let html = String(text);
    
    // First, convert markdown links [text](url) to HTML links
    // This must be done before converting plain URLs to avoid double conversion
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
    
    // Convert **bold** to <strong>bold</strong>
    // Use non-greedy matching to handle multiple bold sections
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert plain URLs to clickable links
    // Only process URLs that are not already inside <a> tags
    // We'll do this by processing the string and checking context
    const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
    let lastIndex = 0;
    const parts: string[] = [];
    let match;
    
    // Reset regex lastIndex
    urlRegex.lastIndex = 0;
    
    while ((match = urlRegex.exec(html)) !== null) {
      const url = match[0];
      const matchIndex = match.index;
      
      // Add text before the URL
      if (matchIndex > lastIndex) {
        parts.push(html.substring(lastIndex, matchIndex));
      }
      
      // Check if this URL is already inside an <a> tag
      const beforeUrl = html.substring(0, matchIndex);
      const lastOpenTag = beforeUrl.lastIndexOf('<a');
      const lastCloseTag = beforeUrl.lastIndexOf('</a>');
      
      if (lastOpenTag > lastCloseTag) {
        // Already inside an anchor tag, don't convert
        parts.push(url);
      } else {
        // Not inside an anchor tag, convert to link
        // Check for trailing punctuation
        const trailingPunctuation = /([.,;:!?\)\]\}]+)$/;
        const punctuationMatch = url.match(trailingPunctuation);
        if (punctuationMatch) {
          const cleanUrl = url.replace(trailingPunctuation, '');
          const punctuation = punctuationMatch[1];
          parts.push(`<a href="${cleanUrl}">${cleanUrl}</a>${punctuation}`);
        } else {
          parts.push(`<a href="${url}">${url}</a>`);
        }
      }
      
      lastIndex = matchIndex + url.length;
    }
    
    // Add remaining text
    if (lastIndex < html.length) {
      parts.push(html.substring(lastIndex));
    }
    
    html = parts.join('');
    
    // Convert line breaks to <br> tags
    html = html.split('\n').map(line => line.trim() === '' ? '<br>' : line).join('<br>');
    
    return html;
  };

  // Wrap HTML body content in a proper HTML document structure
  // Some email clients (like Follow Up Boss) expect a complete HTML document
  const wrapHtmlDocument = (bodyContent: string): string => {
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
</head>
<body>
${bodyContent}
</body>
</html>`;
  };

  // Detect Windows OS
  const isWindows = (): boolean => {
    return navigator.platform.toLowerCase().includes('win') || 
           navigator.userAgent.toLowerCase().includes('windows');
  };

  const copyEmail = async () => {
    // Ensure emailContent is a string
    if (!emailContent || typeof emailContent !== 'string') {
      toast({
        title: "Error",
        description: "No email content to copy",
        variant: "destructive",
      });
      return;
    }

    const emailText = String(emailContent);

    // Convert markdown to HTML (just the body content)
    const htmlBody = markdownToHtml(emailText);
    
    // Wrap in HTML document structure for better email client compatibility
    const htmlDocument = wrapHtmlDocument(htmlBody);
    
    // Create a plain text version (remove markdown formatting)
    const plainText = emailText
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 $2'); // Convert [text](url) to "text url"
    
    const isWin = isWindows();
    let tempDiv: HTMLDivElement | null = null;
    let selection: Selection | null = null;
    
    try {
      // On Windows, prioritize execCommand method (works better with Follow Up Boss)
      // On other platforms, try ClipboardItem first, then fall back to execCommand
      if (isWin) {
        // Windows: Try execCommand first for better email client compatibility (Follow Up Boss)
        tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlBody; // Use body content, not full document for execCommand
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '0';
        tempDiv.setAttribute('contenteditable', 'true');
        document.body.appendChild(tempDiv);
        
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        // Use execCommand (works well with email clients like Follow Up Boss)
        const success = document.execCommand('copy');
        
        if (success) {
          toast({
            title: "Copied!",
            description: "Email copied to clipboard with formatting",
          });
          return;
        }
        
        // Clean up tempDiv if execCommand failed
        if (tempDiv && document.body.contains(tempDiv)) {
          document.body.removeChild(tempDiv);
          tempDiv = null;
        }
        if (selection) {
          selection.removeAllRanges();
          selection = null;
        }
        
        // Fallback: Try ClipboardItem with wrapped HTML document
        if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
          try {
            const clipboardItem = new ClipboardItem({
              'text/html': new Blob([htmlDocument], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' })
            });
            
            await navigator.clipboard.write([clipboardItem]);
            
            toast({
              title: "Copied!",
              description: "Email copied to clipboard with formatting",
            });
            return;
          } catch (clipboardError) {
            logger.debug('ClipboardItem failed on Windows:', clipboardError);
            throw new Error('All copy methods failed');
          }
        } else {
          throw new Error('execCommand copy failed and ClipboardItem not available');
        }
      } else {
        // Non-Windows: Try ClipboardItem API first
        if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
          try {
            const clipboardItem = new ClipboardItem({
              'text/html': new Blob([htmlDocument], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' })
            });
            
            await navigator.clipboard.write([clipboardItem]);
            
            toast({
              title: "Copied!",
              description: "Email copied to clipboard with formatting",
            });
            return;
          } catch (clipboardError) {
            // Fall through to execCommand method
            logger.debug('ClipboardItem failed, trying execCommand:', clipboardError);
          }
        }
        
        // Fallback: Use temporary element with execCommand
        tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlBody;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '0';
        tempDiv.setAttribute('contenteditable', 'true');
        document.body.appendChild(tempDiv);
        
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        // Use execCommand (works well with email clients)
        const success = document.execCommand('copy');
        
        if (success) {
          toast({
            title: "Copied!",
            description: "Email copied to clipboard with formatting",
          });
        } else {
          throw new Error('execCommand copy failed');
        }
      }
    } catch (error) {
      logger.error('Copy error:', error);
      // Final fallback to plain text
      try {
        await navigator.clipboard.writeText(plainText);
        toast({
          title: "Copied!",
          description: "Email copied to clipboard (plain text)",
        });
      } catch (fallbackError) {
        logger.error('Fallback copy failed:', fallbackError);
        toast({
          title: "Error",
          description: "Failed to copy email to clipboard",
          variant: "destructive",
        });
      }
    } finally {
      // Clean up
      if (selection) {
        selection.removeAllRanges();
      }
      if (tempDiv && document.body.contains(tempDiv)) {
        document.body.removeChild(tempDiv);
      }
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(reportUrl);
    toast({
      title: "Copied!",
      description: "Link copied to clipboard",
    });
  };

  const copySubject = () => {
    navigator.clipboard.writeText(emailSubject);
    toast({
      title: "Copied!",
      description: "Subject copied to clipboard",
    });
  };

  const sendEmail = async () => {
    if (!recipientEmail || !emailContent) return;

    // Split by comma, trim whitespace, filter empty strings
    const emails = recipientEmail.split(",").map((e) => e.trim()).filter(Boolean);

    if (emails.length === 0) return;

    // Validate each email individually
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter((e) => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      toast({
        title: "Invalid Email Address" + (invalidEmails.length > 1 ? "es" : ""),
        description: `Please fix: ${invalidEmails.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    setSendSuccess(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const emailText = String(emailContent);
      const htmlBody = wrapHtmlDocument(markdownToHtml(emailText));
      const plainText = emailText
        .replace(/\*\*/g, "")
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 $2");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-report-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            northId,
            recipientEmails: emails,
            subject: emailSubject,
            htmlBody,
            plainTextBody: plainText,
            signatureScope: sendScope,
            ...(sendScope === "agent" && agentName ? { agentName } : {}),
          }),
        }
      );

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to send email");
      }

      setSendSuccess(true);
      const sentCount = result.sentCount || emails.length;
      toast({
        title: "Email Sent",
        description: sentCount === 1
          ? `Report email sent to ${emails[0]}`
          : `Report email sent to ${sentCount} recipients`,
      });
    } catch (error) {
      logger.error("Send email error:", error);
      toast({
        title: "Send Failed",
        description: error instanceof Error ? error.message : "Failed to send email",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-5xl max-h-[90vh] overflow-y-auto rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">Share</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Email Section */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-yellow-400 rounded-full">
                  <Mail className="h-4 w-4 md:h-5 md:w-5 text-yellow-900" />
                </div>
                <h3 className="text-sm md:text-base font-semibold">Email the Seller</h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={copyLink}
                  variant="outline"
                  size="sm"
                >
                  <LinkIcon className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                  <span className="text-xs md:text-sm">Copy Link</span>
                </Button>
                <Button
                  onClick={generateEmail}
                  disabled={isGenerating || isTyping}
                  size="sm"
                  className="relative overflow-hidden bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white border-0 transition-all duration-300 hover:scale-105"
                >
                  <Sparkles className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                  <span className="text-xs md:text-sm">Regenerate</span>
                </Button>
                <Button
                  onClick={copyEmail}
                  disabled={!emailContent || isGenerating || isTyping}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Copy className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                  <span className="text-xs md:text-sm">Copy Email</span>
                </Button>
              </div>
            </div>

              {/* Email Preview */}
            <div className="border rounded-lg p-3 md:p-4 bg-background h-[400px] md:h-[500px] relative overflow-y-auto">
              {/* Mac-style window controls */}
              <div className="flex gap-1.5 md:gap-2 mb-3 md:mb-4">
                <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-500" />
                <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-500" />
                <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500" />
              </div>

              {/* Subject line */}
              <div className="mb-3 md:mb-4 pb-2 md:pb-3 border-b">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs md:text-sm text-muted-foreground">Subject: </span>
                    <span className="text-xs md:text-sm font-medium break-words">{emailSubject}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copySubject}
                    className="h-6 px-2 flex-shrink-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Copy button for email body */}
              {displayedContent && !isGenerating && (
                <div className="flex justify-end mb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyEmail}
                    className="h-6 px-2"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {/* Email body */}
              <div className="text-xs md:text-sm whitespace-pre-wrap leading-relaxed min-h-[250px] md:min-h-[300px]">
                {isGenerating && !emailContent && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3 w-3 md:h-4 md:w-4 animate-spin" />
                    <span className="text-xs md:text-sm">Generating email...</span>
                  </div>
                )}
                {displayedContent && (
                  <div className="space-y-2">
                    {displayedContent.split('\n').map((line, idx) => (
                      <div key={idx}>
                        {formatText(line)}
                      </div>
                    ))}
                    {(isGenerating || isTyping) && <span className="inline-block w-0.5 h-3 md:h-4 bg-foreground animate-pulse ml-0.5">|</span>}
                  </div>
                )}

                {/* Signature preview via iframe for CSS isolation */}
                {currentSignature?.signatureHtml && displayedContent && !isGenerating && (
                  <div
                    className="mt-4 pt-3 border-t border-gray-300"
                  >
                    <iframe
                      srcDoc={`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{margin:0;font-family:system-ui,-apple-system,sans-serif;font-size:13px;}</style></head><body>${currentSignature.signatureHtml}</body></html>`}
                      className="w-full border-0"
                      style={{ minHeight: "60px" }}
                      title="Email signature"
                      onLoad={(e) => {
                        const iframe = e.target as HTMLIFrameElement;
                        if (iframe.contentDocument?.body) {
                          iframe.style.height = iframe.contentDocument.body.scrollHeight + "px";
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Send Email Section */}
            {!credentialsLoading && hasCredentials && (
              <div className="border-t pt-4 space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="text"
                    placeholder="Recipient email addresses (separate with commas)"
                    value={recipientEmail}
                    onChange={(e) => {
                      setRecipientEmail(e.target.value);
                      setSendSuccess(false);
                    }}
                    className="flex-1 text-sm"
                  />
                  {/* Split send button: main action + dropdown for scope selection */}
                  <div className="flex items-stretch">
                    <Button
                      onClick={sendEmail}
                      disabled={!emailContent || !recipientEmail || isGenerating || isTyping || isSending}
                      className={
                        (sendSuccess
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white") +
                        (matchedAgent ? " rounded-r-none" : "")
                      }
                      size="sm"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2 animate-spin" />
                          <span className="text-xs md:text-sm">Sending...</span>
                        </>
                      ) : sendSuccess ? (
                        <>
                          <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                          <span className="text-xs md:text-sm">Sent!</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                          <span className="text-xs md:text-sm">
                            {sendScope === "agent" && agentName
                              ? `Send as ${agentName}`
                              : "Send as Team"}
                          </span>
                        </>
                      )}
                    </Button>
                    {matchedAgent && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            className={
                              (sendSuccess
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : "bg-blue-600 hover:bg-blue-700 text-white") +
                              " rounded-l-none border-l border-l-white/20 px-1.5"
                            }
                            disabled={isSending}
                          >
                            <ChevronDown className="h-3 w-3 md:h-4 md:w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => { setSendScope("team"); setSendSuccess(false); }}
                            disabled={!teamSignature}
                            className={!teamSignature ? "opacity-50" : ""}
                          >
                            Send from Team
                            {!teamSignature && <span className="ml-1 text-xs text-muted-foreground">(no signature)</span>}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setSendScope("agent"); setSendSuccess(false); }}
                            disabled={!agentSignature}
                            className={!agentSignature ? "opacity-50" : ""}
                          >
                            Send as {agentName}
                            {!agentSignature && <span className="ml-1 text-xs text-muted-foreground">(no signature)</span>}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
